import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  useGetCurrentApplicationQuery,
  useGetOpenIDConfigurationQuery
} from 'services/applicationApi';
import { WebStorageStateStore } from 'oidc-client-ts';
import { setToken } from 'slice/authSlice';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { AuthProvider } from 'react-oidc-context';
import OIDCAutoSignIn from 'components/Security/OIDCAutoSignIn';
import ErrorFallback from 'pages/template/PortalTemplate';
import IndeterminateLoading from 'components/Loading/IndeterminateLoading';
import { cleanRelativeUrl } from 'utils/urlUtils';
import { useAuth } from 'hook/useAuth';

/**
 * Provides an OpenID Connect authentication layer for the application. It initializes
 * the OpenID Connect configuration by fetching it remotely and setting up the authentication
 * context for the application. If the application or the OpenID configuration is not secured,
 * it renders the children components directly. Otherwise, it wraps the children components
 * with an `AuthProvider` to manage the authentication flow.
 *
 * @component
 * @param {object} props - The component props.
 * @param {React.ReactNode} props.children - The children components to be rendered within
 * the authentication context.
 *
 * @returns {React.ReactElement} The OpenID authentication provider component which either
 * directly renders the children or wraps them with authentication context providers based
 * on the application's security configuration.
 *
 * @example
 * <OpenIDAuthProvider>
 *   <App />
 * </OpenIDAuthProvider>
 */
function OIDCAuthProvider({ children }) {
  const { formatMessage: msg } = useIntl();
  const dispatch = useDispatch();
  const { logout } = useAuth();

  const currentLocation =
    window.location.origin +
    cleanRelativeUrl(window.location, ['state', 'session_state', 'code'], ['app']);

  const [openIDConfigurationInitialized, setOpenIDConfigurationInitialized] = useState(false);
  const [openIDConfiguration, setOpenIDConfiguration] = useState({
    enabled: false,
    automaticSilentRenew: true,
    redirect_uri: currentLocation,
    post_logout_redirect_uri: currentLocation,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    onSigninCallback: (user) => {
      dispatch(setToken(user?.id_token));
      // Clean the openID redirect Url
      window.history.replaceState(
        {},
        document.title,
        cleanRelativeUrl(window.location, ['state', 'session_state', 'code'], ['app'])
      );
    }
  });

  const {
    data: appConf = null,
    error: appConfError = false,
    isLoading: appConfLoading = true
  } = useGetCurrentApplicationQuery();

  const { data: openIDRemoteConf, error: openIDConfError } = useGetOpenIDConfigurationQuery();

  useEffect(() => {
    if (openIDRemoteConf) {
      setOpenIDConfiguration((currentConfig) => ({
        ...currentConfig,
        ...{ enabled: openIDRemoteConf.enabled },
        ...openIDRemoteConf.parameters
      }));
      setOpenIDConfigurationInitialized(true);
    }
  }, [openIDRemoteConf]);

  if (openIDConfError) {
    const error = {
      message: msg({ id: 'error.openID.configuration' }),
      stack: openIDConfError.message ?? 'openIDConfError',
      status: openIDConfError.status
    };
    console.warn(JSON.stringify(error));
    logout(true);
    return null;
  }

  if (appConfError) {
    const error = {
      message: msg({ id: 'error.openID.connect' }),
      stack: appConfError.message
    };
    if (!openIDConfigurationInitialized && !openIDConfError) {
      return (
        <IndeterminateLoading
          sx={{ width: '100%' }}
          message={msg({ id: 'loading.configuration' })}
        />
      );
    }
    if (openIDRemoteConf?.enabled) {
      return null;
    }
    return <ErrorFallback error={error} />;
  }

  if (!openIDConfigurationInitialized || appConfLoading) {
    return (
      <IndeterminateLoading sx={{ width: '100%' }} message={msg({ id: 'loading.configuration' })} />
    );
  }

  if (openIDConfiguration?.enabled && appConf?.secured) {
    return (
      <AuthProvider {...openIDConfiguration}>
        <OIDCAutoSignIn>{children}</OIDCAutoSignIn>
      </AuthProvider>
    );
  }

  return children;
}

OIDCAuthProvider.propTypes = {
  children: PropTypes.node
};

export default OIDCAuthProvider;
